import clsx from "clsx";

import { SearchInputFilterItems } from "features/filters/components";
import { LABEL_COLORS } from "features/labels/constants";
import useAllLabels from "features/labels/datahooks/useAllLabels";

import ProfileImage from "common/components/ui/ProfileImage";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useLeadsFacet from "common/datahooks/useLeadsFacet";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";
import { getSearchParamFacetKey } from "common/helpers/utils";
import useLeadsSearchParams from "common/hooks/useLeadsSearchParams";
import useSortedFacets from "common/hooks/useSortedFacets";
import type { LeadsSearchParams, SelectedFacetParam } from "common/types";

interface LeadsFilterOptionsProps {
  searchParamsKey: keyof Omit<
    LeadsSearchParams<never>,
    "keywords" | "favorite"
  >;
  searchParams: LeadsSearchParams<SelectedFacetParam>;
  setSearchParams: (
    searchParams: Partial<LeadsSearchParams<SelectedFacetParam>>,
  ) => void;
}

export default function LeadsFilterOptions({
  searchParamsKey,
  searchParams,
  setSearchParams,
}: LeadsFilterOptionsProps) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { toggleSearchParam } = useLeadsSearchParams(
    searchParams,
    setSearchParams,
  );

  const { facetData, isFetchingFacet } = useLeadsFacet(
    getSearchParamFacetKey(searchParamsKey),
    searchParams,
  );
  const { workspaceAccounts } = useWorkspaceAccounts(workspaceId);
  const { allLabels } = useAllLabels("workspace");

  const sortedFacets = useSortedFacets(
    facetData || [],
    searchParams[searchParamsKey],
  );

  function getFilteredItems(lowercaseSearchKeyword: string) {
    return sortedFacets.filter(({ id, name }) => {
      let facetLabel = name;

      if (searchParamsKey === "lead_of") {
        const account = workspaceAccounts.find((account) => account.id === id);
        facetLabel = account ? account.full_name : "Missing account";
      }

      return (
        !lowercaseSearchKeyword ||
        facetLabel.toLowerCase().includes(lowercaseSearchKeyword)
      );
    });
  }

  return (
    <SearchInputFilterItems
      getFilteredItems={getFilteredItems}
      renderLabel={({ name, id }) => {
        if (searchParamsKey === "lead_of") {
          const account = workspaceAccounts.find(
            (account) => account.id === id,
          );
          return account ? account.full_name : "Missing account";
        }
        return name;
      }}
      itemCountKey="target_count"
      renderLabelPrefixComponent={(facet) => {
        if (searchParamsKey === "lead_of") {
          const account = workspaceAccounts.find(({ id }) => id === facet.id);
          return <ProfileImage size="2xs" src={account?.picture_url} />;
        } else if (searchParamsKey === "labels") {
          const label = allLabels.find(({ id }) => id === facet.id);

          if (label) {
            return (
              <div
                className={clsx(
                  "m-0.5 size-3.5 rounded shrink-0",
                  LABEL_COLORS[label.color].bgClassName,
                )}
              />
            );
          }
        }
      }}
      getIsChecked={({ id }) =>
        !!searchParams[searchParamsKey].find(
          (facetParam) => facetParam.id === id,
        )
      }
      onSelectionChange={(type, item) => {
        if (type === "toggle") {
          toggleSearchParam(searchParamsKey, item);
        } else {
          setSearchParams({ ...searchParams, [searchParamsKey]: [item] });
        }
      }}
      isLoading={isFetchingFacet}
    />
  );
}
